import { getActivePrestationBrandConfig } from "../config/environment.config";

/** Plafond URSSAF : 8 cours par client et par mois calendaire, tous prestataires confondus. */
export const MAX_COURSES_PER_MONTH = 8;

export interface MonthlyQuota {
    /** Cours déclarés ce mois pour ce client, tous prestataires confondus. */
    total: number;
    /** Dont déclarés par le prof connecté. */
    mine: number;
    /** Dont déclarés par d'autres prestataires (jamais nommés). */
    others: number;
    remaining: number;
    reached: boolean;
    /** Il ne reste qu'un cours. */
    nearLimit: boolean;
}

/**
 * Compteur mensuel à partir des deux valeurs renvoyées par l'API
 * (coursesThisRollingMonth : tous profs, coursesMonthByThisTeachr : le prof
 * connecté). Null quand le total est inconnu (client sans fiche validée).
 */
export const monthlyQuotaOf = (total: number | null | undefined, mine: number | null | undefined): MonthlyQuota | null => {
    if (total == null) return null;
    const safeTotal = Math.max(0, total);
    const safeMine = Math.min(safeTotal, Math.max(0, mine ?? 0));
    const remaining = Math.max(0, MAX_COURSES_PER_MONTH - safeTotal);
    return {
        total: safeTotal,
        mine: safeMine,
        others: safeTotal - safeMine,
        remaining,
        reached: remaining === 0,
        nearLimit: remaining === 1,
    };
};

export interface MonthlyQuotaText {
    /** « 5 restants / 8 » ou « Quota atteint ». */
    headline: string;
    /** « 3 déclarés ce mois : 1 par vous, 2 par d'autres profs ». */
    detail: string;
    tone: "default" | "warning" | "danger";
}

const plural = (count: number, word: string) => (count > 1 ? `${word}s` : word);

/**
 * Textes du compteur, identiques partout (liste, fiche, choix de l'élève,
 * déclaration) : le prof voit ce qu'il reste au client sur les 8 et ce qu'il a
 * lui-même déclaré. Les autres prestataires ne sont jamais nommés.
 */
export const describeMonthlyQuota = (quota: MonthlyQuota | null, options: { compact?: boolean } = {}): MonthlyQuotaText => {
    const prestation = getActivePrestationBrandConfig();
    if (quota === null) {
        return { headline: "-", detail: `${MAX_COURSES_PER_MONTH} ${getActivePrestationBrandConfig("s")} max par mois`, tone: "default" };
    }
    const headline = quota.reached
        ? "Quota atteint"
        : `${quota.remaining} ${plural(quota.remaining, "restant")} / ${MAX_COURSES_PER_MONTH}`;
    let detail: string;
    if (quota.total === 0) {
        detail = `${getActivePrestationBrandConfig("", "Aucun ", "Aucune ")} ce mois`;
    } else if (options.compact) {
        // cellule de tableau : une seule information, la part du prof
        detail = quota.others > 0 ? `dont ${quota.mine} par vous` : `${quota.mine} par vous`;
    } else {
        const parts = [`${quota.mine} par vous`];
        if (quota.others > 0) parts.push(`${quota.others} par ${quota.others > 1 ? "d'autres profs" : "un autre prof"}`);
        // « déclarés » pour des cours, « déclarées » pour des prestations
        const feminine = prestation === "prestation" ? "e" : "";
        detail = `${quota.total} déclaré${feminine}${quota.total > 1 ? "s" : ""} ce mois : ${parts.join(", ")}`;
    }
    return {
        headline,
        detail,
        tone: quota.reached ? "danger" : quota.nearLimit ? "warning" : "default",
    };
};
